/* eslint-disable no-unused-vars */
import { SupabaseClientService } from 'src/services';
import {
  GetTapTapCustomersPayload,
  GetTapTapReportPayload,
  TapTapColumn,
  TapTapCustomerData,
  TapTapCustomerPayment,
  TapTapFiltersV2,
  TapTapGetCustomersCondition,
  TapTapImageFieldJson
} from './types';
import { getRandomUUIDv4Short, getTime } from 'src/utils';
import {
  CreateTransactionPaymentRequest,
  GetTransactionsViaTransactionNoResponse,
  NewBranchData,
  PermissionApiName,
  TemplateProductDetails,
  TransactionPayment
} from 'src/types';
import {
  quickFilterViewsChoices,
  quickFilterViewsLabels,
  taptapBranchReleased,
  taptapSalesTransferBranch
} from './constants';
import { cloneDeep, every, isEqual, some } from 'lodash';
import {
  taptapFetchCustomers,
  taptapGetCustomerDetailsService,
  taptapFetchEODReport
} from './services';
import { taptapCustomerField } from './constants/taptap-fields';
import { PCWorthBranchesEnum } from 'src/enums/branches';
import {
  quickFilterViewsChoicesV2,
  taptapStatuses
} from './constants/taptap-flow-process';

const defaultIncludedTicketStatuses = Object.values(taptapStatuses).filter(
  (status) =>
    status !== taptapStatuses.COMPLETED && status !== taptapStatuses.CANCELLED
);

const isCreatedAtFilterCondition = (condition: TapTapGetCustomersCondition) =>
  condition.field === taptapCustomerField.CREATED_AT &&
  (condition.operator === 'gte' || condition.operator === 'lte');

const getSalesTransferBranchKeywordViaBranchReleased = (branch?: string) => {
  switch (branch) {
    case taptapBranchReleased.EARNSHAW:
      return taptapSalesTransferBranch.SAMPALOC;
    case taptapBranchReleased.LPC:
      return taptapSalesTransferBranch.LPC;
    case taptapBranchReleased.EXPERIENCE:
      return 'QC';
    default:
      return '';
  }
};

const getBranchReleasedOrSalesTransferConditions = (branches: string[]) => {
  const branchConditions =
    branches.length > 0
      ? [`${taptapCustomerField.BRANCH_RELEASED}.in.(${branches.join(',')})`]
      : [];
  const salesTransferConditions = branches
    .map(getSalesTransferBranchKeywordViaBranchReleased)
    .filter(Boolean)
    .map(
      (branchKeyword) =>
        `${taptapCustomerField.KIND_OF_SALES_TRANSFER}.ilike.%${branchKeyword}%`
    );

  return [...branchConditions, ...salesTransferConditions].join(',');
};

const isCustomerVisibleViaBranchOrSalesTransfer = (
  customer: TapTapCustomerData,
  branches: string[]
) => {
  if (branches.includes(customer.branch_released || '')) {
    return true;
  }

  return branches
    .map(getSalesTransferBranchKeywordViaBranchReleased)
    .filter(Boolean)
    .some((branchKeyword) =>
      customer.kind_of_sales_transfer
        ?.toLowerCase()
        ?.includes(branchKeyword.toLowerCase())
    );
};

export const taptapUploadMultipleImages = async (
  path: string,
  images: TapTapImageFieldJson[]
) => {
  let uploadedImgs: TapTapImageFieldJson[] = [];
  const userId = (await SupabaseClientService?.auth?.getUser()).data.user?.id;

  try {
    for (const image of images) {
      if (image.file) {
        const randomFileName = `${getRandomUUIDv4Short()}-${Date.now()}`;
        const { error } = await SupabaseClientService.storage
          .from('taptap-images')
          .upload(`${path}${randomFileName}`, image.file); // TODO: Change the path based on field key

        if (error) {
          console.error(`Error uploading image for field ${path}:`, error);
          throw new Error(`Failed to upload images for field ${path}.`);
        }

        // IMPORTANT
        image.file = undefined; // Remove the file object

        // Get the public URL
        const imgUrlResp = SupabaseClientService.storage
          .from('taptap-images')
          .getPublicUrl(`${path}${randomFileName}`);

        uploadedImgs.push({
          ...image,
          img_url: imgUrlResp.data.publicUrl,
          created_by: userId,
          created_at: new Date().toISOString()
        });
      } else {
        uploadedImgs.push(image); // Already uploaded images remain unchanged
      }
    }

    console.log('All images uploaded successfully:', uploadedImgs);
    return uploadedImgs;
  } catch (err) {
    console.error('Upload process terminated:', err);
    return null; // Or handle the error as needed
  }
};

export const onTapTapDeleteCustomerPaymentsViaCustomerId = async (
  customerId: number | undefined
) => {
  if (!customerId) {
    console.error('Customer ID is undefined or null.');
    return;
  }

  const dataForDeletion = {
    deleted_at: new Date().toISOString(),
    deleted_by: 0,
    is_deleted: true
  };

  const response = await SupabaseClientService.from('customers_payments')
    .update(dataForDeletion)
    .filter('is_deleted', 'eq', false)
    .filter('customer_id', 'eq', customerId);

  if (response?.error) {
    console.error(`Error deleting customer payments:`, response?.error);
  }

  return response;
};

export const onTapTapCustomerTransactionInfoChangeViaInternal = async (
  transactionInfo: GetTransactionsViaTransactionNoResponse
) => {
  const { data: customerData, error } = await SupabaseClientService.from(
    'customers'
  )
    .update({
      total_amount: transactionInfo?.sub_total_price,
      // Syempre pag 0 na yung total price, dapat null na yung internal transaction no
      internal_transaction_no:
        transactionInfo?.sub_total_price === 0
          ? null
          : transactionInfo?.transaction_no
    })
    .filter('is_deleted', 'eq', false)
    .filter('internal_transaction_no', 'eq', transactionInfo?.transaction_no)
    .select('id');

  if (error) {
    console.error(`Error updating customer transaction info:`, error);
    return;
  }

  if (
    !transactionInfo?.sub_total_price ||
    transactionInfo?.sub_total_price === 0
  ) {
    const dataForDeletion = {
      deleted_at: new Date().toISOString(),
      deleted_by: 0,
      is_deleted: true
    };

    const { error: errCustomerPayments } = await SupabaseClientService.from(
      'customers_payments'
    )
      .update(dataForDeletion)
      .filter('is_deleted', 'eq', false)
      .filter('internal_transaction_no', 'eq', transactionInfo?.transaction_no);

    if (errCustomerPayments) {
      console.error(`Error removing customer payment:`, error);
    }
  }

  if (customerData?.[0]?.id) {
    const customerId = customerData?.[0]?.id;
    onUpdateTapTapBalanceAndPaidAmount(customerId);
  }
  console.log('customerData', customerData);
  // onUpdateTapTapBalanceAndPaidAmount();
};

export const onTapTapCustomerPaymentDeleteViaInternal = async ({
  paymentToBeDeleted,
  transactionNo,
  userDetailsId
}: {
  paymentToBeDeleted?: TransactionPayment;
  transactionNo?: string;
  userDetailsId?: number;
}) => {
  const dataForDeletion = {
    deleted_at: new Date().toISOString(),
    deleted_by: userDetailsId,
    is_deleted: true
  };

  const { data, error } = await SupabaseClientService.from('customers_payments')
    .update(dataForDeletion)
    .filter('is_deleted', 'eq', false)
    .filter('internal_transaction_no', 'eq', transactionNo)
    .filter('payment_type', 'eq', paymentToBeDeleted?.payment_type)
    .filter('amount', 'eq', paymentToBeDeleted?.amount)
    .select('customer_id');

  if (error) {
    console.error(`Error adding customer payment:`, error);
  } else {
    if (data?.[0]?.customer_id) {
      onUpdateTapTapBalanceAndPaidAmount(data?.[0]?.customer_id);
    }
  }
};

export const onTapTapCustomerPaymentAddedViaInternal = async (
  dataArg: CreateTransactionPaymentRequest
) => {
  const {
    data: customerData,
    error: customerError
  } = await SupabaseClientService.from('customers')
    .select('id')
    .eq('internal_transaction_no', dataArg?.transaction_no)
    .filter('is_deleted', 'eq', false)
    .single();

  if (customerError) {
    console.error(`customerError`, customerError);
  }

  const newCustomerPaymentData: TapTapCustomerPayment = {
    amount: dataArg?.amount,
    payment_type: dataArg?.payment_type,
    customer_id: customerData?.id,
    internal_transaction_no: dataArg?.transaction_no,
    acknowledgement_receipt_no: dataArg?.acknowledgement_receipt_no
  };

  const response = await SupabaseClientService.from(
    'customers_payments'
  ).insert({ ...newCustomerPaymentData });

  const { error } = response;

  if (error) {
    console.error(`Error adding customer payment:`, error);
    return;
  }

  onUpdateTapTapBalanceAndPaidAmount(customerData?.id);
};

export const onTapTapCustomerPaymentAddFromTransactionPayments = async (
  dataArg: TransactionPayment[],
  otherArgs: { transaction_no: string; taptap_customer_id: number },
  previousData?: TapTapCustomerPayment[]
) => {
  const transformedData = transactionPaymentsToTapTapCustomerPaymentPayloadTransformer(
    dataArg,
    otherArgs,
    previousData
  );
  const { data, error } = await SupabaseClientService.from('customers_payments')
    .insert(transformedData)
    .select('customer_id');

  if (error) {
    console.error(`Error adding customer payment:`, error);
  }

  if (data?.[0]?.customer_id) {
    onUpdateTapTapBalanceAndPaidAmount(data?.[0]?.customer_id);
  }
};

export const transactionPaymentsToTapTapCustomerPaymentPayloadTransformer = (
  dataArg: TransactionPayment[] = [],
  otherArgs?: { transaction_no: string; taptap_customer_id: number },
  previousCustomerPayments: TapTapCustomerPayment[] = []
) => {
  const remainingPrevious = [...previousCustomerPayments];

  const transformedData: TapTapCustomerPayment[] = dataArg
    ?.filter((x) => !x?.deleted_by)
    ?.map((payment) => {
      const matchedIndex = remainingPrevious.findIndex((prev) => {
        const isBasicMatch =
          prev?.payment_type === payment?.payment_type &&
          prev?.amount === payment?.amount &&
          prev?.acknowledgement_receipt_no ===
            payment?.acknowledgement_receipt_no;

        if (!isBasicMatch) return false;

        const prevTime = getTime(prev?.created_at);
        const paymentTime = getTime(payment?.date_posted);

        if (prevTime && paymentTime) {
          return prevTime === paymentTime;
        }

        return true;
      });

      const matchedPrevious =
        matchedIndex !== -1
          ? remainingPrevious.splice(matchedIndex, 1)[0]
          : null;

      return {
        amount: payment?.amount,
        payment_type: payment?.payment_type,
        customer_id: otherArgs?.taptap_customer_id,
        internal_transaction_no: otherArgs?.transaction_no,
        acknowledgement_receipt_no: payment?.acknowledgement_receipt_no,
        created_at: payment?.date_posted
          ? new Date(payment?.date_posted).toISOString()
          : new Date().toISOString(),

        screenshots: matchedPrevious?.screenshots ?? []
      };
    });

  return transformedData;
};

export const findSelectedQuickFilterViaParam = (
  filterParam?: TapTapFiltersV2
) => {
  const selectedQuickFilter = quickFilterViewsChoices?.find(
    (quickFilterViewParam) => {
      const allConditionsExist = every(
        quickFilterViewParam?.filters?.conditions,
        (firstCondition) =>
          some(filterParam?.conditions, (secondCondition) =>
            isEqual(firstCondition, secondCondition)
          )
      );

      return allConditionsExist;
    }
  );
  return selectedQuickFilter || null;
};

export const updateColumnVisibilityUtil = (
  columns: TapTapColumn[] = [],
  defaultColumns: string[]
) => {
  const clonedColumns = cloneDeep(columns);

  const transformedColumns = clonedColumns.map((column) => ({
    ...column,
    visible: defaultColumns.includes(column.field)
  }));

  transformedColumns.sort((a, b) => {
    const indexA = defaultColumns.indexOf(a.field);
    const indexB = defaultColumns.indexOf(b.field);
    return (
      (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
    );
  });

  return transformedColumns;
};

/**
 * A util that is called whenever we update the `balance` of a customer
 */
export const onUpdateTapTapBalanceAndPaidAmount = async (
  customer_id?: number
) => {
  if (!customer_id) {
    return;
  }
  const { data, error } = await taptapGetCustomerDetailsService(customer_id);

  if (error) {
    console.error('Error fetching payments:', error);
    return;
  }

  if (data && data?.[0]) {
    const customerData = data[0];
    const customerPayments: TapTapCustomerPayment[] =
      customerData?.customers_payments || [];

    // Calculate total payments
    const paidAmount = (customerPayments || []).reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );

    // Calculate balance
    const balance = (customerData?.total_amount || 0) - paidAmount;
    const { error: updateError } = await SupabaseClientService.from('customers')
      .update({
        [taptapCustomerField.PAID_AMOUNT]: paidAmount,
        [taptapCustomerField.BALANCE]: balance
      })
      .eq('id', customer_id);

    if (updateError) {
      console.error('Error updating balance and paid_amount', error);
      return;
    }
  }
};

// Helper function to evaluate individual conditions
export const evaluateConditionLocally = (
  data: TapTapCustomerData,
  condition: TapTapGetCustomersCondition
) => {
  const { field, operator, value } = condition;

  switch (operator) {
    case 'eq': // Equal
      return data[field] === value;
    case 'neq': // Not equal
      return data[field] !== value;
    case 'lt': // Less than
      return data[field] < value;
    case 'lte': // Less than or equal
      return data[field] <= value;
    case 'gt': // Greater than
      return data[field] > value;
    case 'gte': // Greater than or equal
      return data[field] >= value;
    case 'in': // In array
      return Array.isArray(value) && value.includes(data[field]);
    case 'is': // Is null or not null
      return (
        (value === null && data[field] === null) ||
        (value !== null && data[field] !== null)
      );
    case 'like': // Pattern matching
      return (
        typeof data[field] === 'string' &&
        new RegExp(value.replace('%', '.*')).test(data[field])
      );
    case 'ilike': // Case-insensitive pattern matching
      return (
        typeof data[field] === 'string' &&
        new RegExp(value.replace('%', '.*'), 'i').test(data[field])
      );
    default:
      console.error(`Unsupported operator: ${operator}`);
      return false;
  }
};

/**
 *
 * Checks if the new customer data is valid for insertion because sometimes the inserted data is not following local filter
 *
 * @param newCustomerData
 * @param localFilter
 * @returns boolean
 */
export const isInsertNewCustomerDataIsValid = (
  newCustomerData: TapTapCustomerData,
  localFilterArg: GetTapTapCustomersPayload
) => {
  const localFilter = cloneDeep(localFilterArg);

  // 1. Check keywords
  if (localFilter?.search) {
    const propertiesToCheck = [
      taptapCustomerField.FIRST_NAME,
      taptapCustomerField.LAST_NAME,
      taptapCustomerField.INTERNAL_TRANSACTION_NO
    ];
    const searchKeywordsLowerCase = localFilter?.search?.trim()?.toLowerCase();

    // If may search keyword na, tapos hindi naman siya nasa propertiesToCheck, then return false
    const isSearchKeywordFound = propertiesToCheck.some((property) =>
      newCustomerData[property]
        ?.toLowerCase()
        ?.includes(searchKeywordsLowerCase)
    );

    if (!isSearchKeywordFound) {
      return false;
    }
  }

  // 2. Check branch
  const localFilterSelectedBranch = localFilter?.filters?.conditions?.find(
    (x) => x.field === taptapCustomerField.BRANCH_RELEASED
  )?.value;

  if (
    localFilterSelectedBranch &&
    localFilterSelectedBranch?.length > 0 &&
    newCustomerData.branch_released
  ) {
    const isBranchReleasedValid = isCustomerVisibleViaBranchOrSalesTransfer(
      newCustomerData,
      localFilterSelectedBranch
    );

    if (!isBranchReleasedValid) {
      return false;
    }

    // remove branch released from conditions kasi I think makakagulo siya sa conditions na "or" and "and"
    localFilter.filters.conditions = localFilter.filters.conditions.filter(
      (x) => x.field !== taptapCustomerField.BRANCH_RELEASED
    );
  }

  // 3. Check if "or" or "and" logic
  const isValidViaLocalFilters =
    localFilter.filters.logic === 'and'
      ? localFilter.filters.conditions.every((condition) =>
          evaluateConditionLocally(newCustomerData, condition)
        )
      : localFilter.filters.conditions.some((condition) =>
          evaluateConditionLocally(newCustomerData, condition)
        );

  if (!isValidViaLocalFilters) {
    return false;
  }

  // Nice it passed the local test. Push through!
  return true;
};

export const getTapTapQuickViewFiltersViaPermission = (
  userPermissions: PermissionApiName
) => {
  const filteredViews = quickFilterViewsChoices.filter((view) => {
    return !view.permission || userPermissions?.includes(view.permission);
  });
  return filteredViews;
};

export const getCustomersQueryBuilder = ({
  payloadArg,
  maxRecordCount = 0
}: {
  payloadArg: GetTapTapCustomersPayload;
  maxRecordCount?: number;
  queryBuilderExtraConfig?: { csv?: boolean };
}) => {
  const payload = cloneDeep(payloadArg);
  const searchKeyword = payload.search
    ? payload.search.trim().replace(/[,.:()"\\]/g, '')
    : '';
  const logic = payload.filters?.logic || 'and';
  const originalConditions = payload.filters?.conditions || [];
  // Pagination logic
  const pagination = payload.pagination;
  const currentPage = pagination.page;
  const pageSize = pagination?.pageSize;
  // Sorting related
  const sortingPayload = payload.sort || [];
  let query = taptapFetchCustomers({
    includeDefaultCreatedAtFilters: !searchKeyword
  });

  if (payload.includeAllTicketStatuses === false) {
    query = query.in(taptapCustomerField.STATUS, defaultIncludedTicketStatuses);
  }

  // Handle searchKeyword
  if (searchKeyword) {
    const searchConditions = [
      `${taptapCustomerField.FIRST_NAME}.ilike.%${searchKeyword}%`,
      `${taptapCustomerField.LAST_NAME}.ilike.%${searchKeyword}%`,
      `${taptapCustomerField.INTERNAL_TRANSACTION_NO}.ilike.%${searchKeyword}%`,
      `${taptapCustomerField.FULL_NAME}.ilike.%${searchKeyword}%`
    ].join(',');
    query = query.or(searchConditions);
  }

  // Handle "branch_released" condition and remove it from conditions
  const conditionsForFiltering = searchKeyword
    ? originalConditions.filter(
        (condition) => !isCreatedAtFilterCondition(condition)
      )
    : originalConditions;

  const branchReleasedCondition = conditionsForFiltering.find(
    (condition) => condition.field === taptapCustomerField.BRANCH_RELEASED
  );
  const filteredConditions = conditionsForFiltering.filter(
    (condition) => condition.field !== taptapCustomerField.BRANCH_RELEASED
  );
  if (branchReleasedCondition) {
    const branchReleasedValue = branchReleasedCondition.value;
    const branchOrSalesTransferConditions = getBranchReleasedOrSalesTransferConditions(
      branchReleasedValue
    );

    if (branchOrSalesTransferConditions) {
      query = query.or(branchOrSalesTransferConditions);
    }
  }

  // Handle filters based on logic (AND/OR)
  if (logic === 'or' && filteredConditions.length > 0) {
    const orConditions = filteredConditions
      .map((filter) => {
        const field = filter?.field?.toLowerCase().replace(/\s/g, '_');
        const operator = filter?.operator;
        const value = filter?.value;
        switch (operator) {
          // case 'like':
          //   return `${field}.like.%${value}%`;
          case 'ilike':
            return `${field}.ilike.%${value}%`;
          case 'eq':
          case 'neq':
          case 'lte':
          case 'lt':
          case 'gte':
          case 'gt': // ginagamit and tested
            return `${field}.${operator}.${value}`;
          case 'in': // ginagamit and tested
            return `${field}.in.(${value.join(',')})`;
          default:
            console.error(`Unsupported operator: ${operator}`);
            return null;
        }
      })
      .filter(Boolean) // Remove null or undefined conditions
      .join(',');
    query = query.or(orConditions);
  } else if (logic === 'and' && filteredConditions?.length > 0) {
    filteredConditions.forEach((filter) => {
      const field = filter?.field?.toLowerCase().replace(/\s/g, '_');
      const operator = filter?.operator;
      const value = filter?.value;
      // I tried to make this dynamic before. Did not work. Wasted time
      switch (operator) {
        case 'eq':
          query = query.eq(field, value);
          break;
        case 'neq':
          query = query.neq(field, value);
          break;
        case 'is':
          query = query.is(field, value);
          break;
        case 'like':
          query = query.like(field, `%${value}%`);
          break;
        case 'gte': // ginagamit and tested
          query = query.gte(field, value);
          break;
        case 'lte':
          query = query.lte(field, value);
          break;
        case 'lt': // ginagamit and tested
          query = query.lt(field, value);
          break;
        case 'gt': // ginagamit and tested
          query = query.gt(field, value);
          break;
        case 'in': // ginagamit and tested
          query = query.in(field, value);
          break;
        case 'not-in': // Customized to. Walang ganitong operator kay supabase
          if (Array.isArray(value) && value?.length > 0) {
            const stringOfValArr = `(${value?.join(',')})`;

            query = query.not(field, 'in', stringOfValArr);
          } else {
            console.error(
              `Invalid value format for this customized 'not-in' operator: ${value}`
            );
          }
          break;
        default:
          console.error(`Unsupported operator: ${operator}`);
      }
    });
  }
  // Handle sorting
  if (sortingPayload && sortingPayload.length > 0) {
    sortingPayload.forEach((sort) => {
      query = query.order(sort.field, {
        ascending: sort.direction === 'ASC',
        referencedTable: sort.referencedTable || undefined
      });
    });
  } else {
    // Default sorting if no sorting is provided
    query = query.order('created_at', {
      ascending: true
    });
  }
  // Handle pagination
  const offset = (currentPage - 1) * pageSize;
  const endRange = offset + pageSize - 1;

  query = query.range(offset, endRange);

  // For sales transfer / cashier view taptap testing
  // query = query.not(taptapCustomerField.KIND_OF_SALES_TRANSFER, 'is', 'null');

  return query;
};

export const taptapKindOfSalesTransferViaBranchId = (branchId?: number) => {
  switch (branchId) {
    case PCWorthBranchesEnum.Earnshaw:
      return taptapSalesTransferBranch.SAMPALOC;
    case PCWorthBranchesEnum.Experience:
      // return taptapSalesTransferBranch.QC;
      // For now, use the string 'QC' directly because 'QC EXPERIENCE (MAIN)' might not work due to parentheses
      return 'QC';
    case PCWorthBranchesEnum.LPC:
      return taptapSalesTransferBranch.LPC;
    default:
      return '';
  }
};

export const isInvalidProduct = (product: TemplateProductDetails) => {
  return (
    !product?.product?.product_id &&
    product?.product?.category_name !== 'VOUCHER'
  );
};

export const isPaymentBankTransfer = (paymentType: string) =>
  /[_-]bt/i.test(paymentType);

export const hasExistingVoucherPayment = async (transactionNo: string) => {
  const { data } = await SupabaseClientService.from('customers_payments')
    .select('id')
    .eq('internal_transaction_no', transactionNo)
    .ilike('payment_type', '%VOUCHER%')
    .eq('is_deleted', false)
    .limit(1);

  return data && data.length > 0;
};

// based on getCustomersQueryBuilder
export const getEODReportQueryBuilder = ({
  payloadArg,
  maxRecordCount = 0
}: {
  payloadArg: GetTapTapReportPayload;
  maxRecordCount?: number;
}) => {
  const payload = cloneDeep(payloadArg);
  const logic = payload.filters?.logic || 'and';
  const originalConditions = payload.filters?.conditions || [];
  let query = taptapFetchEODReport();

  // Handle "branch_released" condition and remove it from conditions
  const branchReleasedCondition = originalConditions.find(
    (condition) => condition.field === taptapCustomerField.BRANCH_RELEASED
  );
  const filteredConditions = originalConditions.filter(
    (condition) => condition.field !== taptapCustomerField.BRANCH_RELEASED
  );
  if (branchReleasedCondition) {
    const branchReleasedValue = branchReleasedCondition.value;
    query = query.in(taptapCustomerField.BRANCH_RELEASED, branchReleasedValue);
  }

  // Handle filters based on logic (AND/OR)
  if (logic === 'or' && filteredConditions.length > 0) {
    const orConditions = filteredConditions
      .map((filter) => {
        const field = filter?.field?.toLowerCase().replace(/\s/g, '_');
        const operator = filter?.operator;
        const value = filter?.value;
        switch (operator) {
          // case 'like':
          //   return `${field}.like.%${value}%`;
          case 'ilike':
            return `${field}.ilike.%${value}%`;
          case 'eq':
          case 'neq':
          case 'lte':
          case 'lt':
          case 'gte':
          case 'gt': // ginagamit and tested
            return `${field}.${operator}.${value}`;
          case 'in': // ginagamit and tested
            return `${field}.in.(${value.join(',')})`;
          default:
            console.error(`Unsupported operator: ${operator}`);
            return null;
        }
      })
      .filter(Boolean) // Remove null or undefined conditions
      .join(',');
    query = query.or(orConditions);
  } else if (logic === 'and' && filteredConditions?.length > 0) {
    filteredConditions.forEach((filter) => {
      const field = filter?.field?.toLowerCase().replace(/\s/g, '_');
      const operator = filter?.operator;
      const value = filter?.value;
      switch (operator) {
        case 'eq':
          query = query.eq(field, value);
          break;
        case 'neq':
          query = query.neq(field, value);
          break;
        case 'is':
          query = query.is(field, value);
          break;
        case 'like':
          query = query.like(field, `%${value}%`);
          break;
        case 'gte': // ginagamit and tested
          query = query.gte(field, value);
          break;
        case 'lte':
          query = query.lte(field, value);
          break;
        case 'lt': // ginagamit and tested
          query = query.lt(field, value);
          break;
        case 'gt': // ginagamit and tested
          query = query.gt(field, value);
          break;
        case 'in': // ginagamit and tested
          query = query.in(field, value);
          break;
        case 'not-in': // Customized to. Walang ganitong operator kay supabase
          if (Array.isArray(value) && value?.length > 0) {
            const stringOfValArr = `(${value?.join(',')})`;

            query = query.not(field, 'in', stringOfValArr);
          } else {
            console.error(
              `Invalid value format for this customized 'not-in' operator: ${value}`
            );
          }
          break;
        default:
          console.error(`Unsupported operator: ${operator}`);
      }
    });
  }
  // Default sorting
  query = query.order('created_at', {
    ascending: false
  });

  return query;
};

// =============FOR SIMSO APPLICATION LOGIC==========

export const getTapTapQuickViewFiltersViaPermissionV2 = (
  userPermissions: PermissionApiName[]
) => {
  const canViewAll = userPermissions?.includes('can_view_all_taptap_dashboard');
  const canReadOnlyAdminDashboard = userPermissions?.includes(
    'read_only_taptap_admin_dashboard'
  );

  const filteredViews = quickFilterViewsChoicesV2.filter((view) => {
    return (
      canViewAll ||
      (canReadOnlyAdminDashboard &&
        view.permission === 'can_view_all_taptap_dashboard') ||
      !view.permission ||
      userPermissions?.includes(view.permission)
    );
  });
  return filteredViews;
};

//gagamitin din sa simso IS
export const findSelectedQuickFilterViaParamV2 = (
  filterParam?: TapTapFiltersV2,
  sortParam?: GetTapTapCustomersPayload['sort']
) => {
  void sortParam;

  const selectedQuickFilter = quickFilterViewsChoicesV2?.find(
    (quickFilterViewParam) => {
      const quickFilterConditions =
        quickFilterViewParam?.filters?.conditions || [];
      const currentConditions = (filterParam?.conditions || []).filter(
        (condition) =>
          condition.field !== taptapCustomerField.BRANCH_RELEASED &&
          !(
            condition.field === taptapCustomerField.CREATED_AT &&
            (condition.operator === 'gte' || condition.operator === 'lte')
          )
      );
      const allConditionsExist = every(
        quickFilterConditions,
        (firstCondition) =>
          some(currentConditions, (secondCondition) =>
            isEqual(firstCondition, secondCondition)
          )
      );

      const hasSameConditionCount =
        quickFilterConditions.length === currentConditions.length;
      const hasSameSort = true;

      return allConditionsExist && hasSameConditionCount && hasSameSort;
    }
  );
  return selectedQuickFilter || null;
};

export const getQuickFilterByLabel = (label?: string) => {
  return quickFilterViewsChoicesV2.find((view) => view.label === label);
};

// eslint-disable-next-line no-undef
export const branchIdToCode: Record<number, string> = {
  1: taptapBranchReleased.EARNSHAW,
  2: taptapBranchReleased.LPC,
  4: taptapBranchReleased.EXPERIENCE
};
